"use client";

import Button from "@/components/Button";
import TREATMENTS from "@/constants/treatments";
import { useMobileDetection } from "@/utils/mobile-detection";
import { ArrowRightIcon } from "@heroicons/react/24/solid";
import Image from "next/image";

import TrackedLink from "./components/TrackedLink";
import {
    DesktopTreatmentCard,
    MobileTreatmentCard,
} from "./components/TreatmentCards";

export default function AllTreatments() {
    const isMobile = useMobileDetection();

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-8">
            <div className="text-center">
                <h2 className="text-center text-[#333333] dark:text-white text-3xl md:text-[44px] font-semibold font-satoshi uppercase leading-tight md:leading-[57.20px] mb-2">
                    Explore our range of treatments
                </h2>
                <p className="text-[#333333] dark:text-gray-200 text-base md:text-xl font-normal font-space leading-snug md:leading-7 mx-auto">
                    RTHM provides prescriptions for hard-to-find medications for
                    Long COVID, ME/CFS, POTS, and MCAS.
                </p>
            </div>

            <div
                className={
                    isMobile
                        ? "flex flex-col items-center gap-4"
                        : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                }
            >
                {TREATMENTS.map((treatment, index) =>
                    isMobile ? (
                        <MobileTreatmentCard
                            key={treatment.id}
                            treatment={treatment}
                            index={index}
                        />
                    ) : (
                        <DesktopTreatmentCard
                            key={treatment.id}
                            treatment={treatment}
                            index={index}
                        />
                    ),
                )}
            </div>
            {isMobile ? <MobileDiscoverSection /> : <DesktopDiscoverSection />}
        </div>
    );
}

function MobileDiscoverSection() {
    return (
        <div className="bg-card rounded-xl shadow-md overflow-hidden">
            <div className="bg-[#DFE4EA] dark:bg-gray-600 py-2 flex justify-center">
                <Image
                    src="/images/pills/PillGroup.png"
                    alt="Pills"
                    width={250}
                    height={250}
                    className="object-contain"
                />
            </div>

            <TrackedLink
                href="/discover"
                eventName="discover_my_treatments_clicked"
            >
                <div className="p-4 flex flex-col gap-3">
                    <div className="flex justify-between items-center">
                        <div className="text-[#1a5b77] dark:text-blue-300 text-lg font-medium font-satoshi leading-tight">
                            Don&apos;t know where to start?
                        </div>
                        <ArrowRightIcon className="h-6 w-6 text-[#1a5b77] dark:text-blue-300" />
                    </div>
                    <p className="text-base text-[#333333] dark:text-gray-200 font-light font-space leading-relaxed">
                        We can help! Our treatment discovery tool takes in your
                        symptoms and their severity levels and provides a
                        tailored list of medications that may support your
                        journey back to health.
                    </p>
                    <p className="text-base text-[#333333] dark:text-gray-200 font-light font-space leading-relaxed">
                        Get personalized suggestions based on your specific
                        symptoms and conditions and save time sifting through
                        countless research papers.
                    </p>
                </div>
            </TrackedLink>
        </div>
    );
}

function DesktopDiscoverSection() {
    return (
        <div className="bg-card rounded-2xl p-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-5">
                <div className="w-full md:w-1/2 flex justify-center mb-5 md:mb-0">
                    <Image
                        src="/images/pills/PillGroup.png"
                        alt="Pills"
                        width={0}
                        height={0}
                        sizes="100vw"
                        style={{ width: "100%", height: "auto" }}
                    />
                </div>

                <div className="w-full md:w-1/2 flex flex-col gap-5 text-center md:text-left">
                    <h2 className="text-2xl md:text-[44px] font-semibold font-satoshi text-[#333333] dark:text-white uppercase leading-tight">
                        Don&apos;t know
                        <br className="hidden md:block" />
                        where to start?
                    </h2>

                    <div className="flex flex-col gap-4">
                        <p className="text-base md:text-lg font-light font-space text-[#333333] dark:text-gray-200 leading-relaxed">
                            We can help! Our treatment discovery tool takes in
                            your symptoms and their severity levels and provides
                            a tailored list of medications that may support your
                            journey back to health.
                        </p>
                        <p className="text-base md:text-lg font-light font-space text-[#333333] dark:text-gray-200 leading-relaxed">
                            Get personalized suggestions based on your specific
                            symptoms and conditions and save time sifting
                            through countless research papers.
                        </p>
                    </div>

                    <TrackedLink
                        href="/discover"
                        eventName="discover_my_treatments_clicked"
                    >
                        <Button
                            text="Discover My Treatments"
                            type="filled"
                            id="discover-treatments"
                            className="uppercase px-8"
                        />
                    </TrackedLink>
                </div>
            </div>
        </div>
    );
}
